
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI, Modality } from '@google/genai';
import type { Session as GenAILiveSession, LiveServerMessage } from '@google/genai';
import { Journey, Session, Question, AnswerAttempt, Feedback, KnowledgeDoc } from '@shared/types';
import { evaluateAnswer, generateFollowUp } from '@shared/services/gemini';
import { storage } from '@shared/utils/storage';
import { speakText, stopSpeaking } from '@shared/utils/voice';
import { buildKnowledgeContext } from '@shared/utils/knowledge';
import AnswerModal from './AnswerModal';

interface Props {
  journey: Journey;
  session: Session;
  knowledgeDocs?: KnowledgeDoc[];
  onComplete: (session: Session) => void;
  onExit: () => void;
  onTogglePin: (id: string) => void;
  reviewMode?: boolean;
}

const renderBoldText = (value: string) => {
  const safeValue = value || "";
  const parts = safeValue.split("**");
  return parts.map((part, idx) => idx % 2 === 1 ? (
    <strong key={idx} className="font-black">{part}</strong>
  ) : (
    <span key={idx}>{part}</span>
  ));
};

const InterviewSession: React.FC<Props> = ({ journey, session, knowledgeDocs = [], onComplete, onExit, onTogglePin, reviewMode = false }) => {
  const [currentIdx, setCurrentIdx] = useState(session.currentQuestionIdx || 0);
  const [answer, setAnswer] = useState('');
  const [challengeResponse, setChallengeResponse] = useState('');
  const [localSession, setLocalSession] = useState<Session>(session);
  const [followUp, setFollowUp] = useState<string | null>(session.activeFollowUp || null);
  const [loadingFollowUp, setLoadingFollowUp] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [isShowingReport, setIsShowingReport] = useState(reviewMode || session.isComplete || false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [showMobileMicTip, setShowMobileMicTip] = useState(false);
  const [selectedAnswerData, setSelectedAnswerData] = useState<{
    question: string;
    userAnswer: string;
    hurdleQuestion?: string;
    hurdleAnswer?: string;
  } | null>(null);
  const recognitionRef = useRef<any>(null);
  const liveSessionRef = useRef<GenAILiveSession | null>(null);
  const liveQuestionIdRef = useRef<string | null>(null);
  const liveAwaitingFollowUpRef = useRef(false);
  const liveFollowUpBufferRef = useRef('');
  const liveAwaitingQuestionRef = useRef(false);
  const liveQuestionBufferRef = useRef('');
  const liveAudioQueueRef = useRef<string[]>([]);
  const liveAudioPlayingRef = useRef(false);
  const liveTurnAudioReceivedRef = useRef(false);
  const isRealInterview = !!journey.isRealInterview;
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/i.test(navigator.userAgent);
  const voiceSettings = journey.voiceSettings || {
    voiceName: isIOS ? 'David' : 'Google UK English Female',
    rate: isIOS ? 1.2 : 1.1,
    pitch: 1.05
  };
  const [isVoiceMode, setIsVoiceMode] = useState(!!journey.isRealInterview);
  const [liveStatus, setLiveStatus] = useState<'idle' | 'connecting' | 'ready' | 'error'>('idle');
  const [liveError, setLiveError] = useState<string | null>(null);
  const liveDisabled = true; // Use local TTS only; Live is disabled


  const [feedbacks, setFeedbacks] = useState<Record<string, Feedback>>(() => {
    const initial: Record<string, Feedback> = {};
    session.attempts.forEach(a => {
      if (a.feedback) initial[a.questionId] = a.feedback;
    });
    return initial;
  });

  const currentQuestion = journey.questions[currentIdx] || journey.questions[0];
  const isPinned = currentQuestion?.isPinned;

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-GB';
      recognitionRef.current.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) finalTranscript += event.results[i][0].transcript;
        }
        if (followUp) {
          setChallengeResponse(prev => prev + finalTranscript);
        } else {
          setAnswer(prev => prev + finalTranscript);
        }
      };
      recognitionRef.current.onend = () => setIsListening(false);
    }
  }, [followUp]);

  const enqueueLiveAudio = (base64Data: string, mimeType?: string) => {
    try {
      const binary = atob(base64Data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      const blob = new Blob([bytes], { type: mimeType || 'audio/wav' });
      const url = URL.createObjectURL(blob);
      liveAudioQueueRef.current.push(url);
      if (!liveAudioPlayingRef.current) {
        playNextLiveAudio();
      }
    } catch (e) {
      console.warn('Live audio decode failed', e);
    }
  };

  const playNextLiveAudio = () => {
    const nextUrl = liveAudioQueueRef.current.shift();
    if (!nextUrl) {
      liveAudioPlayingRef.current = false;
      return;
    }
    liveAudioPlayingRef.current = true;
    const audio = new Audio(nextUrl);
    audio.onended = () => {
      URL.revokeObjectURL(nextUrl);
      playNextLiveAudio();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(nextUrl);
      playNextLiveAudio();
    };
    audio.play().catch(err => {
      console.warn('Live audio playback blocked', err);
      URL.revokeObjectURL(nextUrl);
      playNextLiveAudio();
    });
  };

  const handleLiveMessage = (msg: LiveServerMessage) => {
    const parts = msg.serverContent?.modelTurn?.parts || [];
    for (const part of parts) {
      if (part.inlineData?.data && part.inlineData?.mimeType?.startsWith('audio/')) {
        liveTurnAudioReceivedRef.current = true;
        enqueueLiveAudio(part.inlineData.data, part.inlineData.mimeType);
      }
    }

    if (liveAwaitingQuestionRef.current) {
      if (msg.text) {
        liveQuestionBufferRef.current += msg.text;
      }
      if (msg.serverContent?.turnComplete) {
        const questionText = liveQuestionBufferRef.current.trim() || 'Question delivered by voice.';
        const hadAudio = liveTurnAudioReceivedRef.current;
        liveTurnAudioReceivedRef.current = false;
        liveAwaitingQuestionRef.current = false;
        liveQuestionBufferRef.current = '';
        if (!hadAudio) speakText(questionText, voiceSettings);
      }
      return;
    }

    if (liveAwaitingFollowUpRef.current) {
      if (msg.text) {
        liveFollowUpBufferRef.current += msg.text;
      }
      if (msg.serverContent?.turnComplete) {
        const followUpText = liveFollowUpBufferRef.current.trim() || 'Follow-up question delivered by voice.';
        const hadAudio = liveTurnAudioReceivedRef.current;
        liveTurnAudioReceivedRef.current = false;
        liveAwaitingFollowUpRef.current = false;
        liveFollowUpBufferRef.current = '';
        setFollowUp(followUpText);
        setLoadingFollowUp(false);
        syncSessionToCloud({
          ...localSession,
          activeFollowUp: followUpText,
          currentQuestionIdx: currentIdx
        });
        if (!hadAudio) speakText(followUpText, voiceSettings);
      }
    }
  };

  const connectLiveSession = async () => {
    if (liveSessionRef.current || liveStatus === 'connecting') return;
    setLiveStatus('connecting');
    setLiveError(null);
    try {
      const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_API_KEY, apiVersion: 'v1alpha' });
      const session = await ai.live.connect({
        model: 'gemini-2.0-flash-live-preview-04-09',
        config: {
          responseModalities: [Modality.AUDIO, Modality.TEXT],
          outputAudioTranscription: {},
          systemInstruction: 'You are a professional NHS interview panelist. Be concise. When asked to ask a question, output only the question. When asked for a follow-up, output exactly one follow-up question.'
        },
        callbacks: {
          onopen: () => setLiveStatus('ready'),
          onmessage: handleLiveMessage,
          onerror: () => {
            setLiveStatus('error');
            setLiveError('Gemini Live connection failed.');
          },
          onclose: () => {
            setLiveStatus(prev => (prev === 'error' ? 'error' : 'idle'));
          }
        }
      });
      liveSessionRef.current = session;
    } catch (e: any) {
      setLiveStatus('error');
      setLiveError(e?.message || 'Gemini Live connection failed.');
    }
  };

  useEffect(() => {
    if (!isRealInterview || liveDisabled) return;
    connectLiveSession();
    return () => {
      liveSessionRef.current?.close();
      liveSessionRef.current = null;
      liveAwaitingQuestionRef.current = false;
      liveQuestionBufferRef.current = '';
      liveAwaitingFollowUpRef.current = false;
      liveFollowUpBufferRef.current = '';
      liveTurnAudioReceivedRef.current = false;
      liveAudioQueueRef.current.forEach(url => URL.revokeObjectURL(url));
      liveAudioQueueRef.current = [];
      liveAudioPlayingRef.current = false;
    };
  }, [isRealInterview, liveDisabled]);

  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  useEffect(() => {
    if (!isVoiceMode || isShowingReport || !currentQuestion?.text) return;
    if (liveDisabled || liveStatus !== 'ready' || !liveSessionRef.current) {
      speakText(currentQuestion.text, voiceSettings);
      return;
    }
    if (liveQuestionIdRef.current === currentQuestion.id) return;
    liveQuestionIdRef.current = currentQuestion.id;
    liveAwaitingQuestionRef.current = true;
    liveQuestionBufferRef.current = '';
    liveSessionRef.current.sendClientContent({
      turns: [{ role: 'user', parts: [{ text: `Ask the following interview question exactly as written, then wait for the candidate's response. Question: "${currentQuestion.text}"` }] }],
      turnComplete: true
    });
  }, [isRealInterview, isShowingReport, currentQuestion?.id, liveStatus]);

  useEffect(() => {
    if (!isVoiceMode || isShowingReport || !followUp) return;
    if (!liveDisabled && liveStatus === 'ready') return;
    speakText(followUp, voiceSettings);
  }, [isVoiceMode, isShowingReport, followUp, liveStatus, liveDisabled, voiceSettings]);

  const toggleListening = () => {
    const isMobile = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && !isListening) {
      setShowMobileMicTip(true);
      setTimeout(() => setShowMobileMicTip(false), 3500);
    }
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setIsListening(true);
      recognitionRef.current?.start();
    }
  };

  const syncSessionToCloud = (updatedSession: Session) => {
    setLocalSession(updatedSession);
    storage.updateSession(updatedSession).catch(err => console.warn("Deferred sync:", err.message));
  };

  const triggerBackgroundEvaluation = (question: Question, attempt: AnswerAttempt) => {
    const knowledgeContext = buildKnowledgeContext(knowledgeDocs, {
      query: `${journey.grade} ${journey.specialty} ${journey.interviewType} ${question.text}`,
      maxChars: 8000
    });

    const evaluationContext = [
      knowledgeContext ? `Core Reference Material:\n${knowledgeContext}` : '',
      journey.candidateProfile ? `Candidate Profile: ${journey.candidateProfile}` : '',
      journey.requirements ? `Job Advert Requirements: ${JSON.stringify(journey.requirements)}` : '',
      journey.additionalDocs?.length ? `Additional Documents: ${journey.additionalDocs.map(d => d.content || '').join('\n')}` : ''
    ].filter(Boolean).join('\n\n');

    evaluateAnswer(
      question,
      attempt.answerText,
      journey.grade,
      journey.specialty,
      attempt.followUpThread,
      evaluationContext
    ).then(evaluation => {
      setFeedbacks(prev => ({ ...prev, [question.id]: evaluation }));
      setLocalSession(prevSession => {
        const updatedAttempts = prevSession.attempts.map(att => 
          att.questionId === question.id ? { ...att, feedback: evaluation } : att
        );
        const newSession = { ...prevSession, attempts: updatedAttempts };
        storage.updateSession(newSession).catch(e => console.warn("Eval sync deferred", e));
        return newSession;
      });
    }).catch(err => console.error("Evaluation error:", err));
  };

  const handleInitialAnswer = async () => {
    if (!answer.trim() || !currentQuestion) return;
    if (isListening) recognitionRef.current?.stop();
    setLoadingFollowUp(true);
    try {
      if (isRealInterview && liveSessionRef.current && liveStatus === 'ready') {
        liveAwaitingFollowUpRef.current = true;
        liveFollowUpBufferRef.current = '';
        liveSessionRef.current.sendClientContent({
          turns: [{ role: 'user', parts: [{ text: `Candidate answer: "${answer}". Ask exactly one follow-up question based on this answer. Output only the question.` }] }],
          turnComplete: true
        });
      } else {
        const challenge = await generateFollowUp(currentQuestion.text, answer, []);
        setFollowUp(challenge);
        const sessionWithHurdle = {
          ...localSession,
          activeFollowUp: challenge,
          currentQuestionIdx: currentIdx
        };
        syncSessionToCloud(sessionWithHurdle);
        setLoadingFollowUp(false);
      }
    } catch (err) {
      setFollowUp("Could you elaborate on how you would maintain patient safety here?");
      setLoadingFollowUp(false);
    } finally {
      if (!isRealInterview) {
        setLoadingFollowUp(false);
      }
    }
  };

  const handleNextStation = () => {
    if (!currentQuestion) return;
    if (isListening) recognitionRef.current?.stop();

    const attempt: AnswerAttempt = {
      questionId: currentQuestion.id,
      answerText: followUp 
        ? `[Initial Answer]\n${answer}\n\n[Hurdle Response]\n${challengeResponse}`
        : answer,
      followUpThread: followUp 
        ? [{ q: currentQuestion.text, a: answer }, { q: followUp, a: challengeResponse }]
        : [{ q: currentQuestion.text, a: answer }],
      timestamp: new Date().toISOString()
    };

    const nextIdx = currentIdx + 1;
    const isEnd = nextIdx >= journey.questions.length;
    
    triggerBackgroundEvaluation(currentQuestion, attempt);

    if (!isEnd) {
      const updatedSession: Session = {
        ...localSession,
        attempts: [...localSession.attempts, attempt],
        currentQuestionIdx: nextIdx,
        activeFollowUp: null
      };
      syncSessionToCloud(updatedSession);
      setCurrentIdx(nextIdx);
      setAnswer('');
      setChallengeResponse('');
      setFollowUp(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      finalizeSessionAndShowReport(attempt);
    }
  };

  const finalizeSessionAndShowReport = (lastMomentAttempt: AnswerAttempt | null = null) => {
    if (isFinishing || isShowingReport) return;
    
    setIsFinishing(true);
    
    const finalAttempts = lastMomentAttempt 
      ? [...localSession.attempts.filter(a => a.questionId !== lastMomentAttempt.questionId), lastMomentAttempt]
      : localSession.attempts;

    const finalizedSession: Session = { 
      ...localSession, 
      attempts: finalAttempts,
      endTime: new Date().toISOString(),
      isComplete: true,
      currentQuestionIdx: 0,
      activeFollowUp: null
    };

    setLocalSession(finalizedSession);
    setCurrentIdx(0);
    storage.updateSession(finalizedSession).catch(e => console.warn("Final sync deferred", e));

    setTimeout(() => {
      setIsFinishing(false);
      setIsShowingReport(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };

  const handleShowAnswer = (question: Question, attempt: AnswerAttempt) => {
    const userAnswer = attempt.followUpThread?.[0]?.a || '';
    const hurdleQuestion = attempt.followUpThread?.[1]?.q;
    const hurdleAnswer = attempt.followUpThread?.[1]?.a;

    setSelectedAnswerData({
      question: question.text,
      userAnswer,
      hurdleQuestion,
      hurdleAnswer
    });
    setShowAnswerModal(true);
  };

  const renderReviewContent = () => {
    const fb = feedbacks[currentQuestion.id];
    const attemptForThisQuestion = localSession.attempts.find(a => a.questionId === currentQuestion.id);
    const isSkipped = !attemptForThisQuestion || !attemptForThisQuestion.answerText.replace(/\[.*?\]/g, '').trim();

    return (
      <div className="space-y-6 sm:space-y-12 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        <div className="bg-white border border-slate-100 rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 shadow-xl shadow-slate-200/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-10 gap-4">
            <div>
              <h3 className="text-xl sm:text-2xl font-black text-slate-900 uppercase tracking-tight">Station Audit</h3>
              <p className={`text-[9px] sm:text-[10px] font-black uppercase tracking-widest mt-1 ${isSkipped ? 'text-amber-500' : 'text-emerald-500'}`}>
                {isSkipped ? 'Status: Station Skipped' : fb ? 'Status: Attempt Analyzed' : 'Status: Panel Reviewing...'}
              </p>
            </div>
            <div className={`flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-[1.5rem] sm:rounded-[2rem] border shadow-inner ${isSkipped ? 'bg-slate-50 border-slate-100' : 'bg-blue-50 border-blue-100'}`}>
              <span className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest">Mastery</span>
              <span className={`text-3xl sm:text-5xl font-black ${isSkipped ? 'text-slate-200' : fb ? 'text-blue-600' : 'text-blue-300 animate-pulse'}`}>
                {isSkipped ? '0' : fb ? fb.overallScore : '...'}<span className="text-xl ml-1">%</span>
              </span>
              {!isSkipped && attemptForThisQuestion && (
                <button
                  onClick={() => handleShowAnswer(currentQuestion, attemptForThisQuestion)}
                  className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 hover:text-emerald-800 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                >
                  Your Answer
                </button>
              )}
            </div>
          </div>

          {!fb ? (
            <div className="py-12 sm:py-20 flex flex-col items-center justify-center bg-blue-50/50 rounded-[2rem] sm:rounded-[3rem] border border-blue-100 border-dashed">
              <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6"></div>
              <p className="text-blue-700 font-black text-[10px] uppercase tracking-[0.2em]">Analyzing feedback...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-10">
               <div className="space-y-6 sm:space-y-8">
                  <div>
                    <p className="text-[10px] font-black text-emerald-600 uppercase mb-3 sm:mb-4 tracking-widest">Strengths</p>
                    <ul className="space-y-2 sm:space-y-3">
                      {fb.pros?.map((p, i) => (
                        <li key={i} className="text-xs sm:text-sm font-bold text-slate-700 bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 shadow-sm flex gap-2">
                          <span className="text-emerald-500">✓</span> {renderBoldText(p)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-red-600 uppercase mb-3 sm:mb-4 tracking-widest">Blind Spots</p>
                    <ul className="space-y-2 sm:space-y-3">
                      {fb.cons?.map((c, i) => (
                        <li key={i} className="text-xs sm:text-sm font-bold text-slate-700 bg-white p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-slate-100 shadow-sm flex gap-2">
                          <span className="text-red-500 font-black">!</span> {renderBoldText(c)}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Hurdle Feedback Section */}
                  {(fb.hurdlePros?.length > 0 || fb.hurdleCons?.length > 0 || fb.hurdleFeedback) && (
                    <div className="border-t border-slate-200 pt-6">
                      <p className="text-[10px] font-black text-amber-600 uppercase mb-3 sm:mb-4 tracking-widest">Hurdle Challenge Feedback</p>
                      {attemptForThisQuestion?.followUpThread?.[1]?.q && (
                        <div className="mb-4 p-3 bg-amber-25 rounded-lg border border-amber-200">
                          <p className="text-[8px] font-black text-amber-700 uppercase mb-2 tracking-widest">Challenge Question</p>
                          <p className="text-xs font-medium text-slate-700 italic">"{attemptForThisQuestion.followUpThread[1].q}"</p>
                        </div>
                      )}
                      <div className="space-y-4">
                        {fb.hurdlePros?.length > 0 && (
                          <div>
                            <p className="text-[8px] font-black text-emerald-600 uppercase mb-2 tracking-widest">Challenge Strengths</p>
                            <ul className="space-y-1">
                              {fb.hurdlePros.map((p, i) => (
                                <li key={i} className="text-xs font-bold text-slate-700 bg-amber-50 p-2 rounded-lg border border-amber-100 flex gap-2">
                                  <span className="text-emerald-500">✓</span> {renderBoldText(p)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {fb.hurdleCons?.length > 0 && (
                          <div>
                            <p className="text-[8px] font-black text-red-600 uppercase mb-2 tracking-widest">Challenge Areas</p>
                            <ul className="space-y-1">
                              {fb.hurdleCons.map((c, i) => (
                                <li key={i} className="text-xs font-bold text-slate-700 bg-amber-50 p-2 rounded-lg border border-amber-100 flex gap-2">
                                  <span className="text-red-500 font-black">!</span> {renderBoldText(c)}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {fb.hurdleFeedback && (
                          <div>
                            <p className="text-[8px] font-black text-amber-700 uppercase mb-2 tracking-widest">Hurdle Assessment</p>
                            <div className="text-xs font-medium text-slate-700 bg-amber-50 p-3 rounded-lg border border-amber-100 italic">
                              {renderBoldText(fb.hurdleFeedback)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {fb.blindSpots?.length > 0 && (
                    <div className="border-t border-slate-200 pt-6">
                      <p className="text-[10px] font-black text-red-600 uppercase mb-3 sm:mb-4 tracking-widest">Mastery Gaps</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {fb.blindSpots.map((spot, i) => (
                          <div key={i} className="text-xs sm:text-sm font-bold text-slate-700 bg-red-50 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-red-100 flex gap-2">
                            <span className="text-red-500 font-black">!</span> {renderBoldText(spot)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
               </div>

               <div className="bg-slate-900 rounded-[2rem] sm:rounded-[3rem] p-6 sm:p-10 text-white shadow-2xl flex flex-col">
                  <p className="text-[10px] font-black text-blue-400 uppercase mb-4 sm:mb-6 tracking-widest">Consultant Answer</p>
                  <div className="text-xs sm:text-sm font-medium leading-relaxed italic text-slate-300 bg-white/5 p-5 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] border border-white/10 shadow-inner mb-6 sm:mb-8">
                    "{renderBoldText(fb.suggestedAnswer)}"
                  </div>

                  {/* Hurdle Ideal Answer */}
                  {fb.hurdleIdealAnswer && (
                    <div>
                      <p className="text-[10px] font-black text-amber-400 uppercase mb-4 tracking-widest">Hurdle Ideal Response</p>
                      <div className="text-xs sm:text-sm font-medium leading-relaxed italic text-amber-200 bg-amber-900/20 p-5 sm:p-8 rounded-[1.5rem] sm:rounded-[2rem] border border-amber-500/20 shadow-inner">
                        "{renderBoldText(fb.hurdleIdealAnswer)}"
                      </div>
                    </div>
                  )}
               </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isFinishing) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-white p-6 sm:p-12 text-center">
        <div className="relative mb-8">
            <div className="w-16 h-16 sm:w-24 sm:h-24 border-8 border-slate-800 rounded-full"></div>
            <div className="absolute top-0 left-0 w-16 h-16 sm:w-24 sm:h-24 border-8 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <h2 className="text-2xl sm:text-4xl font-black mb-2 tracking-tight uppercase">Simulation Finished</h2>
        <p className="text-slate-400 font-medium max-w-md italic leading-relaxed text-sm">Preparing full performance analysis...</p>
      </div>
    );
  }

  return (
    <>
    <div className="print:hidden flex flex-col md:flex-row h-auto bg-white relative">
      {/* Sidebar Toggle for Mobile */}
      {(!isVoiceMode || isShowingReport) && (
        <button 
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
          className="md:hidden fixed bottom-6 left-6 z-[60] bg-blue-600 text-white p-4 rounded-full shadow-2xl"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
      )}

      {/* Sidebar (List of questions) */}
      {(!isVoiceMode || isShowingReport) && (
        <div className={`${showMobileSidebar ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 fixed md:static inset-0 z-[55] w-full md:w-80 bg-slate-50 border-r border-slate-100 p-6 sm:p-10 flex flex-col shrink-0 transition-transform duration-300 ease-in-out`}>
          <div className="mb-6 md:mb-10">
             <div className="flex justify-between items-center">
               <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Session Map</h3>
               <button onClick={() => setShowMobileSidebar(false)} className="md:hidden text-slate-400"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
             </div>
             {isShowingReport && (
               <button
                 onClick={() => window.print()}
                 className="mt-3 w-full bg-white border border-slate-200 text-slate-700 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
               >
                 Download PDF
               </button>
             )}
          </div>
        <div className="space-y-2 flex-grow overflow-y-auto md:overflow-visible overflow-x-hidden custom-scrollbar pr-2 pb-20 md:pb-0">
          {journey.questions.map((q, idx) => {
            const isCompleted = localSession.attempts.some(a => a.questionId === q.id);
            const isActive = idx === currentIdx;
            return (
              <div 
                key={q.id}
                onClick={() => {
                  if (isShowingReport || isCompleted || idx === localSession.currentQuestionIdx) {
                    setCurrentIdx(idx);
                    setShowMobileSidebar(false);
                  }
                }}
                className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl text-xs flex items-center justify-between transition-all duration-300 cursor-pointer overflow-hidden ${isActive ? 'bg-blue-600 text-white shadow-lg' : isCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-white text-slate-300 border border-slate-100'}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center font-black ${isActive ? 'bg-blue-500' : 'bg-slate-100 text-slate-400'}`}>{idx + 1}</div>
                  <span className="font-bold uppercase tracking-tighter truncate">{q.domainTags?.[0] || 'Clinical'}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isShowingReport && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin(q.id);
                      }}
                      className={`p-1.5 rounded-lg border ${q.isPinned ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-emerald-100 border-emerald-200 text-emerald-700 hover:bg-emerald-200'}`}
                      title={q.isPinned ? 'Unpin for Revision' : 'Pin for Revision'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 17v5"/><path d="M5 9h14"/><path d="M7 9V3h10v6"/><path d="M9 9l-3 3v1h12v-1l-3-3"/></svg>
                    </button>
                  )}
                  {isCompleted && <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12"/></svg>}
                </div>
              </div>
            );
          })}
          </div>
          <div className="hidden md:block mt-8 space-y-4">
              <button onClick={() => onExit()} className="w-full bg-slate-100 text-slate-400 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:text-slate-600">Save & Exit</button>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-grow flex flex-col relative bg-[#fafbfd] min-h-screen md:min-h-0">
        <div className="p-6 sm:p-12 border-b border-slate-100 bg-white sticky top-0 z-10 shadow-sm">
          <div className="w-full">
            <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
              <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest">
                Station {currentIdx + 1} / {journey.questions.length}
              </span>
              {isRealInterview && !liveDisabled && (
                <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${liveStatus === 'ready' ? 'bg-emerald-50 text-emerald-600' : liveStatus === 'error' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>
                  Live {liveStatus === 'ready' ? 'Connected' : liveStatus === 'error' ? 'Error' : 'Connecting'}
                </span>
              )}
              {!isVoiceMode && (
                <button 
                  onClick={() => onTogglePin(currentQuestion.id)}
                  className={`p-1.5 px-3 rounded-lg border-2 transition-all flex items-center gap-1.5 font-black text-[9px] uppercase tracking-widest ${isPinned ? 'bg-red-600 border-red-600 text-white' : 'bg-white border-red-600 text-red-600'}`}
                >
                  {isPinned ? "Pinned" : "Pin"}
                </button>
              )}
              {!isShowingReport && (
                <button
                  onClick={() => {
                    setIsVoiceMode(prev => {
                      const next = !prev;
                      if (next) {
                        speakText(currentQuestion.text, voiceSettings);
                      }
                      return next;
                    });
                  }}
                  className={`ml-auto px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${isVoiceMode ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300 hover:text-blue-600'}`}
                >
                  {isVoiceMode ? 'Show Questions' : 'Voice Mode'}
                </button>
              )}
            </div>
            <div className="flex items-start gap-4">
              <div className="flex-grow">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-black text-slate-900 leading-tight tracking-tight">
                  {isVoiceMode && !isShowingReport ? 'Real Interview (Voice)' : currentQuestion?.text}
                </h2>
                {isVoiceMode && !isShowingReport && (
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-2">Questions are delivered by voice only.</p>
                )}
                {isRealInterview && !liveDisabled && liveError && (
                  <p className="text-red-500 text-xs font-bold uppercase tracking-widest mt-2">{liveError}</p>
                )}
              </div>
              {!isVoiceMode && (
                <button 
                  onClick={() => speakText(currentQuestion.text, voiceSettings)}
                  className="bg-blue-50 text-blue-600 p-3 sm:p-4 rounded-xl sm:rounded-2xl shrink-0"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex-grow p-4 sm:p-12 pb-32 md:pb-12">
          {isShowingReport ? renderReviewContent() : (
            <div className="animate-in fade-in duration-500">
              {!followUp ? (
                <div className="relative group">
                  <textarea 
                    value={answer}
                    onChange={e => setAnswer(e.target.value)}
                    placeholder={isVoiceMode ? "Speak or type your response..." : "Structure your answer..."}
                    className="w-full h-[300px] sm:h-[450px] p-6 sm:p-12 pb-24 bg-white border-2 border-slate-100 rounded-[2rem] sm:rounded-[4rem] outline-none text-lg sm:text-2xl leading-relaxed shadow-xl focus:border-blue-600 transition-all"
                  ></textarea>
                  <button 
                    onClick={toggleListening}
                    className={`absolute bottom-6 right-6 p-4 sm:p-6 rounded-2xl sm:rounded-[2.5rem] ${isListening ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-400 hover:bg-blue-600 hover:text-white'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                  </button>
                  {showMobileMicTip && (
                    <div className="absolute bottom-20 right-6 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl shadow-xl">
                      Use your keyboard mic to dictate
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                   <div className="p-6 sm:p-10 bg-amber-50 rounded-[2rem] sm:rounded-[3.5rem] border border-amber-200 shadow-xl relative">
                      <p className="text-[10px] font-black text-amber-600 uppercase mb-4 tracking-widest">Hurdle Challenge</p>
                      {!isVoiceMode ? (
                        <p className="text-xl sm:text-3xl font-black text-amber-900 leading-tight mb-8 italic">"{followUp}"</p>
                      ) : (
                        <p className="text-sm font-bold text-amber-800 mb-8">Challenge question delivered by voice.</p>
                      )}
                      <div className="relative">
                        <textarea 
                          value={challengeResponse}
                          onChange={e => setChallengeResponse(e.target.value)}
                          placeholder={isVoiceMode ? "Speak or type your response..." : "Your defense..."}
                          className="w-full h-48 sm:h-64 p-6 sm:p-10 bg-white border-2 border-amber-200 rounded-[1.5rem] sm:rounded-[3rem] outline-none text-base sm:text-xl shadow-inner"
                        ></textarea>
                        <button 
                          onClick={toggleListening}
                          className={`absolute bottom-6 right-6 p-4 rounded-xl sm:rounded-[2rem] ${isListening ? 'bg-red-500 text-white animate-pulse' : 'bg-amber-100 text-amber-500'}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                        </button>
                        {showMobileMicTip && (
                          <div className="absolute bottom-16 right-6 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-xl shadow-xl">
                            Use your keyboard mic to dictate
                          </div>
                        )}
                      </div>
                   </div>
                </div>
              )}
              <div className="mt-8 flex justify-end">
                <button 
                  onClick={!followUp ? handleInitialAnswer : handleNextStation}
                  disabled={(!answer.trim() && !followUp) || (followUp && !challengeResponse.trim())}
                  className="w-full sm:w-auto bg-slate-900 text-white px-10 py-5 rounded-[1.5rem] sm:rounded-[2.5rem] font-black text-sm uppercase tracking-widest hover:bg-blue-600 disabled:opacity-30 transition-all shadow-xl"
                >
                  {!followUp ? (loadingFollowUp ? "Processing..." : "Submit Answer") : (currentIdx === journey.questions.length - 1 ? 'Finalize Report' : 'Next Station')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Answer Modal */}
      {selectedAnswerData && (
        <AnswerModal
          isOpen={showAnswerModal}
          onClose={() => setShowAnswerModal(false)}
          question={selectedAnswerData.question}
          userAnswer={selectedAnswerData.userAnswer}
          hurdleQuestion={selectedAnswerData.hurdleQuestion}
          hurdleAnswer={selectedAnswerData.hurdleAnswer}
        />
      )}
    </div>
    {isShowingReport && (
      <div className="hidden print:block bg-white text-slate-900">
        <div className="p-8 space-y-8">
          <div className="flex items-start justify-between border-b border-slate-200 pb-4">
            <div>
              <h1 className="text-2xl font-black">Completed Interview Report</h1>
              <p className="text-sm text-slate-500 font-medium">
                {journey.specialty} - {journey.grade} - {journey.interviewType}
              </p>
            </div>
            <div className="text-xs text-slate-500 font-semibold text-right">
              <div>{new Date(localSession.endTime || localSession.startTime).toLocaleDateString('en-GB')}</div>
              <div>Stations: {journey.questions.length}</div>
            </div>
          </div>
          <div className="flex justify-end">
            <img src="/forpdf.png" alt="NHS Interview Academy" className="h-10 w-auto object-contain" />
          </div>

          <div className="space-y-6">
            {journey.questions.map((q, idx) => {
              const attempt = localSession.attempts.find(a => a.questionId === q.id);
              const fb = feedbacks[q.id];
              const hurdleQuestion = attempt?.followUpThread?.[1]?.q || '';

              return (
                <div key={q.id} className="border border-slate-200 rounded-2xl p-5 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Station {idx + 1}</p>
                      <h2 className="text-base font-black text-slate-900">{q.text}</h2>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {q.domainTags?.map(tag => (
                          <span key={tag} className="text-[9px] font-black uppercase bg-slate-50 text-slate-500 px-2 py-1 rounded">{tag}</span>
                        ))}
                        <span className={`text-[9px] font-black uppercase px-2 py-1 rounded ${q.difficulty === 'Hard' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'}`}>
                          {q.difficulty}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-black text-slate-500 uppercase">Score</div>
                      <div className="text-2xl font-black text-blue-600">{fb ? fb.overallScore : 0}%</div>
                    </div>
                  </div>

                  {fb && (
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-2">Consultant Answer</p>
                        <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{renderBoldText(fb.suggestedAnswer)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2">Strengths</p>
                        <ul className="space-y-1 text-xs text-slate-700">
                          {(fb.pros || []).map((p, i) => (
                            <li key={i}>- {renderBoldText(p)}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-2">Blind Spots</p>
                        <ul className="space-y-1 text-xs text-slate-700">
                          {(fb.cons || []).map((c, i) => (
                            <li key={i}>- {renderBoldText(c)}</li>
                          ))}
                        </ul>
                      </div>
                      {hurdleQuestion && (
                        <div>
                          <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2">Hurdle Question</p>
                          <p className="text-xs text-slate-700 leading-relaxed">{hurdleQuestion}</p>
                        </div>
                      )}
                      {fb.hurdleIdealAnswer && (
                        <div>
                          <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2">Hurdle Ideal Response</p>
                          <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{renderBoldText(fb.hurdleIdealAnswer)}</p>
                        </div>
                      )}
                      {(fb.hurdleFeedback || (fb.hurdlePros?.length || 0) > 0 || (fb.hurdleCons?.length || 0) > 0) && (
                        <div>
                          <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-2">Challenge Assessment</p>
                          {fb.hurdleFeedback && (
                            <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{renderBoldText(fb.hurdleFeedback)}</p>
                          )}
                          {(fb.hurdlePros?.length || 0) > 0 && (
                            <ul className="mt-2 space-y-1 text-xs text-slate-700">
                              {fb.hurdlePros.map((p, i) => (
                                <li key={i}>- {renderBoldText(p)}</li>
                              ))}
                            </ul>
                          )}
                          {(fb.hurdleCons?.length || 0) > 0 && (
                            <ul className="mt-2 space-y-1 text-xs text-slate-700">
                              {fb.hurdleCons.map((c, i) => (
                                <li key={i}>- {renderBoldText(c)}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                      {fb.blindSpots?.length > 0 && (
                        <div>
                          <p className="text-[10px] font-black text-red-600 uppercase tracking-widest mb-2">Mastery Gaps</p>
                          <ul className="space-y-1 text-xs text-slate-700">
                            {fb.blindSpots.map((spot, i) => (
                              <li key={i}>- {renderBoldText(spot)}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default InterviewSession;
